import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { openaiService } from '../services/openaiService';
import { motion } from 'framer-motion';
import CodeMirror from '@uiw/react-codemirror';
import { html } from '@codemirror/lang-html';
import { dracula } from '@uiw/codemirror-theme-dracula';
import { BetaBadge, BetaMessage } from '../components/BetaDisclaimer';
import { QuickStart } from '../components/QuickStart';
import DeploymentOptions from '../components/DeploymentOptions';

export function WebCrafter() {
  const [code, setCode] = useState('');
  const [preview, setPreview] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('custom');

  const templateCategories = [
    {
      id: 'marketing',
      name: 'Marketing',
      templates: [
        { id: 'splash', name: 'Splash Page', description: 'Modern, attention-grabbing landing pages' },
        { id: 'product-launch', name: 'Product Launch', description: 'Showcase new products or services' },
        { id: 'coming-soon', name: 'Coming Soon', description: 'Build anticipation with countdown timer' }
      ]
    },
    {
      id: 'email',
      name: 'Email Templates',
      templates: [
        { id: 'newsletter', name: 'Newsletter', description: 'Professional email newsletters' },
        { id: 'promotional', name: 'Promotional', description: 'Marketing and sales campaigns' },
        { id: 'transactional', name: 'Transactional', description: 'Order confirmations and updates' }
      ]
    },
    {
      id: 'content',
      name: 'Content Pages',
      templates: [
        { id: 'blog', name: 'Blog Post', description: 'Rich content blog layouts' },
        { id: 'documentation', name: 'Documentation', description: 'Technical documentation pages' },
        { id: 'portfolio', name: 'Portfolio', description: 'Showcase work and projects' }
      ]
    },
    {
      id: 'commerce',
      name: 'E-commerce',
      templates: [
        { id: 'product-page', name: 'Product Page', description: 'Detailed product showcases' },
        { id: 'category', name: 'Category Page', description: 'Product category listings' },
        { id: 'checkout', name: 'Checkout Flow', description: 'Optimized checkout process' }
      ]
    }
  ];

  const quickStartGuides = [
    {
      title: "Marketing Pages",
      description: "Create high-converting landing pages and promotional content.",
      keywords: ["Landing Pages", "Splash Pages", "Product Launches"],
      example: "Example: Generate a product launch page with countdown timer and signup form",
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
        </svg>
      )
    },
    {
      title: "Email Templates",
      description: "Design responsive email templates that work across clients.",
      keywords: ["Newsletters", "Promotional Emails", "Transactional Emails"],
      example: "Example: Create a newsletter template with dynamic content sections",
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      )
    },
    {
      title: "E-commerce Components",
      description: "Build conversion-optimized shopping experiences.",
      keywords: ["Product Pages", "Shopping Cart", "Checkout Flow"],
      example: "Example: Design a product page with image gallery and variants",
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
        </svg>
      )
    }
  ];

  // Helper function to get template-specific prompt
  const getTemplatePrompt = (templateId) => {
    const templates = {
      'splash': 'Create a modern splash page with a hero section, feature highlights, and call-to-action buttons. Include animations and visual effects.',
      'product-launch': 'Design a product launch page with countdown timer, feature showcase, pricing tables, and signup form.',
      'coming-soon': 'Build a coming soon page with countdown timer, email signup, and social media links.',
      'newsletter': 'Create a responsive email newsletter template with header, content sections, and footer. Ensure compatibility across email clients.',
      'promotional': 'Design a promotional email template with product showcase, special offers, and clear call-to-action buttons.',
      'transactional': 'Build a transactional email template with order details, tracking information, and related products section.',
      'blog': 'Create a blog post template with featured image, content area, author bio, and related posts section.',
      'documentation': 'Design a documentation page with navigation sidebar, content area, and code blocks.',
      'portfolio': 'Build a portfolio page with project showcase, filterable gallery, and contact information.',
      'product-page': 'Create a product page with image gallery, variants selection, add to cart functionality, and related products.',
      'category': 'Design a category page with product grid, filters, sorting options, and pagination.',
      'checkout': 'Build a checkout flow with form validation, payment options, and order summary.',
      'custom': ''
    };
    return templates[templateId] || '';
  };

  // Update preview when code changes
  useEffect(() => {
    setPreview(code);
  }, [code]);

  // Generate HTML code based on user prompt
  const handleGenerateCode = async () => {
    if (!prompt.trim() && selectedTemplate === 'custom') {
      toast.error('Please enter a description of what you want to create');
      return;
    }

    setIsProcessing(true);
    try {
      const templatePrompt = getTemplatePrompt(selectedTemplate);
      const finalPrompt = selectedTemplate === 'custom' ? prompt : `${templatePrompt}\n\nCustom requirements: ${prompt}`;

      const response = await openaiService.chat([
        {
          role: 'system',
          content: `You are an expert full-stack developer specializing in modern web development. Generate production-ready, single-page HTML that incorporates these advanced features:

1. Modern Architecture:
   - Use Web Components with Shadow DOM for encapsulated styling
   - Implement CSS Module Scripts for scoped styles
   - Utilize dynamic module imports for code splitting
   - Include comprehensive meta tags for SEO and social sharing
   - Add manifest.json for PWA support

2. Advanced Styling Architecture:
   - Implement CSS Custom Properties with fallbacks for theming
   - Use CSS @layer for style orchestration and cascade management
   - Implement container queries for component-level responsiveness
   - Use :has() and :is() selectors for advanced targeting
   - Apply logical properties (margin-block, padding-inline) for better i18n
   - Utilize custom property units (--size-fluid-1) for fluid scaling

3. Modern Visual Design:
   - Implement glass morphism effects with backdrop-filter
   - Use CSS mask and clip-path for creative shapes
   - Apply gradient overlays with mix-blend-mode
   - Implement advanced border animations
   - Use CSS grid with auto-fit/auto-fill for responsive layouts
   - Apply modern scrollbar styling with ::-webkit-scrollbar
   - Implement custom cursor styles with pointer-events
   - Use perspective and transform-style for 3D effects

4. Advanced Animation Patterns:
   - Implement scroll-driven animations with @scroll-timeline
   - Use CSS @property for animated custom properties
   - Apply spring physics with CSS @keyframes
   - Implement view transitions for page morphing
   - Use IntersectionObserver for reveal animations
   - Apply FLIP animations for layout transitions
   - Implement gesture-based animations
   - Use CSS grid/flex animations with auto-animate

5. Modern UI Patterns:
   - Implement adaptive color schemes with prefers-color-scheme
   - Use CSS containment for performance optimization
   - Apply content-visibility for render optimization
   - Implement CSS subgrid for nested grid layouts
   - Use aspect-ratio for responsive media
   - Apply modern form styling with :focus-visible
   - Implement custom form controls with ::-webkit-appearance
   - Use overscroll-behavior for custom pull-to-refresh

6. Advanced Effects:
   - Implement parallax scrolling with perspective
   - Use CSS paint API for custom backgrounds
   - Apply backdrop-filter for frosted glass effects
   - Implement advanced hover effects with pseudo-elements
   - Use CSS masks for creative reveals
   - Apply custom cursors with pointer-events
   - Implement advanced loading skeletons
   - Use CSS grid masonry with grid-template-rows: masonry

7. Performance & Optimization:
   - Implement content-visibility: auto for off-screen content
   - Use will-change hints strategically
   - Apply CSS containment for layout isolation
   - Implement CSS overscroll-behavior for custom scroll experiences
   - Use CSS contain-intrinsic-size for layout stability
   - Apply font-display: optional for text optimization
   - Implement CSS size queries for container-based styles

8. Accessibility & User Experience:
   - Implement prefers-reduced-motion adaptations
   - Use forced-colors mode support
   - Apply focus-visible styling for keyboard navigation
   - Implement custom focus indicators
   - Use aria-live regions for dynamic content
   - Apply proper heading hierarchy with CSS
   - Implement skip links with :focus-within

9. Modern Typography:
   - Use CSS clamp() for fluid typography
   - Implement variable fonts with font-variation-settings
   - Apply advanced OpenType features with font-feature-settings
   - Use CSS grid for baseline alignment
   - Implement proper hyphenation with hyphens
   - Apply modern font-loading strategies
   - Use CSS text-wrap: balance for better text blocks

10. Responsive Patterns:
    - Implement container queries for component-based breakpoints
    - Use CSS grid subgrid for nested layouts
    - Apply aspect-ratio boxes for media
    - Implement modern card layouts with grid-template-columns
    - Use CSS columns for responsive text flow
    - Apply modern table responsive patterns
    - Implement advanced responsive images with picture

Generate clean, maintainable code that leverages these modern CSS features for a sophisticated, production-ready design.`
        },
        {
          role: 'user',
          content: finalPrompt
        }
      ]);

      if (response.text) {
        const htmlMatch = response.text.match(/<html[^>]*>([\s\S]*)<\/html>/i);
        const newCode = htmlMatch ? htmlMatch[0] : response.text;
        setCode(newCode);
        toast.success('Production-ready code generated!');
      }
    } catch (error) {
      console.error('Error generating code:', error);
      toast.error('Failed to generate code. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Modify existing code based on user prompt
  const handleModifyCode = async () => {
    if (!prompt.trim()) {
      toast.error('Please enter a description of the changes you want to make');
      return;
    }

    if (!code.trim()) {
      toast.error('No code to modify. Generate some code first!');
      return;
    }

    setIsProcessing(true);
    try {
      const response = await openaiService.chat([
        {
          role: 'system',
          content: `You are an expert full-stack developer specializing in modern web development. Modify the existing code while maintaining or enhancing these advanced features:

1. Architecture Integrity:
   - Preserve Web Component structure and Shadow DOM
   - Maintain module patterns and code organization
   - Keep SEO optimizations and meta tags
   - Preserve or enhance security measures

2. Style Consistency:
   - Maintain CSS Custom Properties and theming
   - Preserve responsive design patterns
   - Keep performance optimizations
   - Maintain dark mode support
   - Preserve animation and transition logic

3. Performance Standards:
   - Maintain resource hints and loading optimizations
   - Preserve service worker functionality
   - Keep critical CSS and lazy loading
   - Maintain image optimization patterns

4. Interactive Elements:
   - Preserve scroll-driven animations
   - Maintain intersection observer logic
   - Keep touch and gesture support
   - Preserve keyboard navigation

5. Quality Standards:
   - Maintain accessibility compliance
   - Preserve structured data
   - Keep error boundaries
   - Maintain proper event handling
   - Preserve memory management

Make precise modifications while maintaining the production-ready quality of the code.`
        },
        {
          role: 'user',
          content: `Current code:\n${code}\n\nRequested changes:\n${prompt}`
        }
      ]);

      if (response.text) {
        const htmlMatch = response.text.match(/<html[^>]*>([\s\S]*)<\/html>/i);
        const newCode = htmlMatch ? htmlMatch[0] : response.text;
        setCode(newCode);
        toast.success('Code modified successfully!');
      }
    } catch (error) {
      console.error('Error modifying code:', error);
      toast.error('Failed to modify code. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Modern Hero Header */}
      <div className="relative mb-8 p-8 rounded-3xl bg-gradient-to-r from-blue-900 via-blue-800 to-blue-900 shadow-2xl overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff0a_1px,transparent_1px),linear-gradient(to_bottom,#ffffff0a_1px,transparent_1px)] bg-[size:24px_24px]"></div>
          <div className="absolute left-0 right-0 top-0 h-px bg-gradient-to-r from-transparent via-blue-400/20 to-transparent"></div>
          <div className="absolute left-0 right-0 bottom-0 h-px bg-gradient-to-r from-transparent via-blue-400/20 to-transparent"></div>
          
          <div className="absolute -left-48 -top-48 h-96 w-96">
            <div className="absolute inset-0 rotate-45 translate-x-1/2 translate-y-1/2 bg-gradient-conic from-blue-500/40 via-blue-300/10 to-blue-400/40 blur-xl opacity-30"></div>
          </div>
          <div className="absolute -right-48 -bottom-48 h-96 w-96">
            <div className="absolute inset-0 rotate-45 translate-x-1/2 translate-y-1/2 bg-gradient-conic from-blue-400/40 via-blue-300/10 to-blue-500/40 blur-xl opacity-30"></div>
          </div>
        </div>

        {/* Content */}
        <div className="relative">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="space-y-4">
              <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-blue-800/50 border border-blue-700/50 backdrop-blur-sm">
                <div className="w-2 h-2 rounded-full bg-blue-400 animate-pulse"></div>
                <span className="text-sm text-blue-100">AI-Powered Development</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white flex items-center">
                WebCrafter
                <BetaBadge className="ml-3" />
              </h1>
              <p className="text-lg text-blue-200 max-w-xl">
                Create beautiful, modern websites with AI assistance. Design and preview in real-time.
              </p>
              <BetaMessage />
              <div className="flex items-center gap-4 pt-2">
                <div className="flex items-center space-x-2 text-sm text-blue-200">
                  <svg className="w-5 h-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Live Preview</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-blue-200">
                  <svg className="w-5 h-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Modern Design</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .bg-gradient-conic {
          background-image: conic-gradient(var(--tw-gradient-stops));
        }
      `}</style>

      <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
        <div className="p-6">
          {/* Template Categories */}
          <div className="mb-8">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Choose a Template</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {templateCategories.map((category) => (
                <div key={category.id} className="space-y-3">
                  <h4 className="text-sm font-medium text-gray-700">{category.name}</h4>
                  <div className="space-y-2">
                    {category.templates.map((template) => (
                      <button
                        key={template.id}
                        onClick={() => {
                          setSelectedTemplate(template.id);
                          setPrompt('');
                        }}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors duration-200 ${
                          selectedTemplate === template.id
                            ? 'bg-blue-50 text-blue-700 border border-blue-200'
                            : 'hover:bg-gray-50 text-gray-700'
                        }`}
                      >
                        <div className="font-medium">{template.name}</div>
                        <div className="text-xs text-gray-500 mt-0.5">{template.description}</div>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-gray-700">Custom</h4>
                <button
                  onClick={() => setSelectedTemplate('custom')}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors duration-200 ${
                    selectedTemplate === 'custom'
                      ? 'bg-blue-50 text-blue-700 border border-blue-200'
                      : 'hover:bg-gray-50 text-gray-700'
                  }`}
                >
                  <div className="font-medium">Custom Design</div>
                  <div className="text-xs text-gray-500 mt-0.5">Create your own custom design</div>
                </button>
              </div>
            </div>
          </div>

          {/* Prompt Input */}
          <div className="space-y-8">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {selectedTemplate === 'custom' ? 'Describe Your Component or Page' : 'Additional Requirements (Optional)'}
              </label>
              <div className="mb-2">
                <div className="text-sm text-gray-500">
                  {selectedTemplate === 'custom'
                    ? 'Example: "Create a responsive product card component with lazy-loaded images, intersection observer animations, and dark mode support"'
                    : 'Add any specific requirements or modifications to the template'}
                </div>
              </div>
              <div className="mt-1 flex space-x-2">
                <input
                  type="text"
                  id="prompt"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="Describe what you want to create..."
                  className="flex-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                />
                <button
                  onClick={code ? handleModifyCode : handleGenerateCode}
                  disabled={isProcessing}
                  className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${
                    isProcessing
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                  }`}
                >
                  {isProcessing ? (
                    <div className="flex items-center">
                      <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Processing...
                    </div>
                  ) : code ? (
                    'Modify'
                  ) : (
                    'Generate'
                  )}
                </button>
              </div>
            </div>

            {/* Editor and Preview Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Code Editor */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900">Code Editor</h3>
                <div className="border rounded-lg overflow-hidden bg-gray-900">
                  <CodeMirror
                    value={code}
                    height="600px"
                    theme={dracula}
                    extensions={[html()]}
                    onChange={(value) => setCode(value)}
                    className="text-sm"
                  />
                </div>
              </div>

              {/* Preview */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-gray-900">Live Preview</h3>
                  <button
                    onClick={() => {
                      const win = window.open('', '_blank');
                      win.document.write(preview);
                    }}
                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Open in New Tab
                  </button>
                </div>
                <div className="border rounded-lg bg-white h-[600px] overflow-auto">
                  <iframe
                    srcDoc={preview}
                    title="Preview"
                    className="w-full h-full"
                    sandbox="allow-scripts"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <QuickStart guides={quickStartGuides} />
      <DeploymentOptions appId="web-crafter" position="none" />
    </div>
  );
} 